import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CommonService, TranslationService, InitService, Product } from 'gast-common-ng';
import { FooterModel } from 'gast-portal-ng/lib/footer/model/footer-model';
import { HeaderModel } from 'gast-portal-ng/lib/header/model/header-model';
// tslint:disable-next-line:max-line-length
import { AuthService, ServiceResponseDto, AuthOperation, ResponseType, CommonPortalUrlService, UrlParamService } from 'gast-portal-ng';
import { LanguageModel } from 'gast-portal-ng/lib/header/model/language-model';
import { UserModel } from 'gast-portal-ng/lib/header/model/user-model';
import { NavTabUrlModel } from 'gast-portal-ng/lib/header/model/nav-tab-url-model';
import { HeaderStyleModel } from 'gast-portal-ng/lib/header/model/header-style-model';
import { TextModel } from 'gast-portal-ng/lib/shared/dto/text-model';
import { LinkModel } from 'gast-portal-ng/lib/shared/dto/link-model';
import { FloatingContactPanelModel } from 'gast-portal-ng/lib/floating-contact-panel/model/floating-contact-panel-model';

import { ShoppingCartService } from './shared/services/shopping-cart.service';
import { HeaderService } from './shared/services/header.service';
import { BookingStepService } from './shared/services/booking-step.service';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import {LanguageDto} from 'gast-common-ng';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {

  subscription = new Subscription();

  // tslint:disable:max-line-length
  // global configuration
  textAlignment = 'left';
  leadingColorCode = '#D63517';
  footerModel: FooterModel;
  headerModel: HeaderModel;
  headerStyle: HeaderStyleModel;
  isDFH = false;
  floatingContactPanelModel: FloatingContactPanelModel;

  languages: Array<LanguageDto>;
  user: UserModel;
  env: any;

  showHeader = true;
  showLogin = true;
  readonly duoUrl = 'https://www.deutsch-uni.com/';

  readonly dfnUrl = environment.dfnEntryPoint;

  constructor(
    private initService: InitService,
    private languageService: CommonService,
    private translationService: TranslationService,
    private translateService: TranslateService,
    private toastr: ToastrService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private shoppingCartService: ShoppingCartService,
    private headerService: HeaderService,
    private urlParamService: UrlParamService,
    private commonPortalUrlService: CommonPortalUrlService,
    private bookingStepService: BookingStepService,
    @Inject('env') private envConfig: any
  ) {
    this.env = this.envConfig.callback(this.envConfig.value);
    this.footerModel = this.generateFooter();
    this.headerModel = this.generateHeaderModel(); // reinitialized after languages loaded and after language in translation service selected
    this.headerStyle = this.generateHeaderStyleModel();
    this.floatingContactPanelModel = this.generateFloatingContactPanelModel();
  }

  ngOnInit() {
    // initialize app
    this.initService.setProductId(Product.DUO);

    // handling session id in url
    const authParamSubscription = this.route.queryParamMap.subscribe(paramMap => {
      this.urlParamService.saveSessionFromParamAndRemoveParam(paramMap);
      this.authService.getUserInfo();
    });

    // callback for query parameter
    const urlParamSubscription = this.route.queryParams.subscribe(params => {
      // GAPTER-526 hide language dropdown for eks
      // TODO: remove once EKS has english texts
      if (params['code']) {
        const code = params['code'] as string;
        if (code === 'DFH') {
          this.isDFH = true;
        }
        this.bookingStepService.loadWebshopConfig(code);
      }
      if (params.lang) {
        this.urlParamService.restoreUrlParams(params.lang);
      }
      // Service calls
      this.languageService.loadLanguages();
    });

    // Subscriptions
    // GAPTER-526 hide language dropdown for eks
    // TODO: remove once EKS has english texts
    const configSubscription = this.bookingStepService.configLoaded.subscribe(config => {
      if (config && config.name.toLowerCase().includes('eks')) {
        // default lang for eks
        this.changeLanguage('de');
        this.headerStyle = this.generateHeaderStyleModel(false);
      }
    });

    const logoutSubscription = this.authService.getAuthServiceResponse().pipe(
      filter(response => response instanceof ServiceResponseDto && response.operation === AuthOperation.Logout))
      .subscribe(response => {
        if (response.type === ResponseType.Success) {
          this.router.navigate(['/course'],
            {
              relativeTo: this.route,
              queryParams: { lang: this.translationService.getCurrentLanguageCode().toLocaleLowerCase() },
            });
        }
        if (response.type === ResponseType.Error) {
          this.toastr.error(this.translateService.instant('GAST-COMMON-NG.GAST-ERROR-CODE.' + response.value.error.code));
        }
      });

    // subscribe to language change
    const languageChangedSubscription = this.translateService.onLangChange.subscribe(event => {
      this.headerModel = this.generateHeaderModel();
      this.footerModel = this.generateFooter();
      this.floatingContactPanelModel = this.generateFloatingContactPanelModel();
    });

    const languageLoadedSubscription = this.languageService.languagesLoaded.subscribe(languages => {
      this.languages = languages;
      if (this.isDFH) {
        if (this.languages.filter(l => l.code === 'fr').length === 0) {
          const french = {
            name: 'Français',
            code: 'fr'
          } as LanguageDto;
          this.languages.push(french);
        }
      }
      if (!this.translateService.currentLang) {
        this.translateService.currentLang = 'de';
      }
      this.headerModel = this.generateHeaderModel();
    });

    // subscribe to header visibility changes
    const headerVisibilitySubscription = this.headerService.showHeaderChanged.subscribe((showHeader) => {
      setTimeout(() => {
        this.showHeader = showHeader;
        if (!showHeader) {
          this.headerModel = null;
        }
      }, 0);
    });

    const headerLoginSubscription = this.headerService.showLoginChanged.subscribe((showLogin) => {
      setTimeout(() => {
        this.showLogin = showLogin;
        if (!showLogin) {
          this.headerStyle = this.generateHeaderStyleModel(false, false);
          this.headerModel = this.generateHeaderModel(false);
        }
      }, 0);
    });

    // add subs
    this.subscription.add(authParamSubscription);
    this.subscription.add(urlParamSubscription);
    this.subscription.add(configSubscription);
    this.subscription.add(logoutSubscription);
    this.subscription.add(languageChangedSubscription);
    this.subscription.add(languageLoadedSubscription);
    this.subscription.add(headerVisibilitySubscription);
    this.subscription.add(headerLoginSubscription);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ********************** EVENT HANDLING **********************
  login(): void {
    const url = this.isBookingProcess()
      ? this.commonPortalUrlService.getPortalAccountLoginUrl(this.initService.getProductFromLocalStorage(), this.translationService)
      : this.commonPortalUrlService.getPortalAccountLoginUrl(this.initService.getProductFromLocalStorage(), this.translationService, this.env.duoLearnerPortalUrl);
    window.location.href = url;
  }

  private isBookingProcess(): boolean {
    if (this.router.url.includes('code=') || this.router.url.includes('voucher=')) {
      return true;
    }
    return this.router.url !== '/course' && !this.router.url.includes('/course?') && !this.router.url.includes('/course/course-detail');
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/course'], { queryParamsHandling: 'merge' });
  }

  logoPressed(): void {
    window.location.href = `https://www.deutsch-uni.com/${this.translationService.getCurrentLanguageCode()}/`;
  }

  onShoppingCartClicked(): void {
    // TODO: Refactor in DWS-1010
    const model = this.shoppingCartService.getShoppingCartModel();
    const isAuthorized = this.getUserModel() && this.getUserModel().username;
    if (model && model.selectedCourseId && model.selectedTimeScheduleId && isAuthorized) {
      this.router.navigate(['course/shopping-cart'], { queryParamsHandling: 'merge' });
    } else if (!isAuthorized) {
      this.toastr.info(this.translateService.instant('COMMON.SHOPPING_CARD_ACCESS'));
    } else {
      this.toastr.info(this.translateService.instant('COMMON.SHOPPING_CARD_EMPTY'));
    }
  }

  search(searchText: string): void {
    console.log(searchText);
    alert(searchText);
  }

  /**
   * Change language and update current lang url parameter
   * @param languageCode
   */
  changeLanguage(languageCode: string): void {
    this.translationService.changeLanguageUsingCode(languageCode);
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: { lang: languageCode.toLocaleLowerCase() },
        queryParamsHandling: 'merge'
      });
  }

  subscribe(email: string): void {
    console.log(email);
    alert(email);
  }

  // *********************** OTHER *****************************
  getUserModel(): UserModel {
    const userDto = this.authService.readUserInfo();
    return userDto ? { username: userDto.username } as UserModel : null;
  }

  // ***************** COMPONENT CONFIGURATION *****************
  private generateHeaderModel(showNavItem = true): HeaderModel {
    if (showNavItem) {
      return {
        languageModels: this.languages ? this.languages.map(l => {
          return { visibleText: l.name, code: l.code } as LanguageModel;
        }) : [],
        navigationTabUrls: [
          { useTranslation: true, displayText: 'COMMON.HEADER.SEARCH', linkUrl: `${this.duoUrl}${this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.SEARCH')}/`, iconClass: 'fal fa-search' },
          { useTranslation: true, displayText: 'COMMON.HEADER.CONTACT', linkUrl: `${this.duoUrl}${this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.CONTACT')}/`, iconClass: 'fal fa-envelope' }
        ] as Array<NavTabUrlModel>,
        loginDropdownUrls: [
          { useTranslation: true, text: 'COMMON.HEADER.MY_DUO', linkUrl: `${this.env.accountPortalUrl}/login?product-id=4&lang=${this.translateService.currentLang}` }
        ] as Array<LinkModel>,
        loginDropdownProductUrls: [
          { useTranslation: false, text: 'TestDaF', linkUrl: 'https://www.testdaf.de' },
          { useTranslation: false, text: 'TestAS', linkUrl: 'https://www.testas.de' },
          { useTranslation: false, text: 'onSET', linkUrl: 'https://www.onset.de' }
        ] as Array<LinkModel>,
        logoBigUrl: 'portal-assets/img/duo_big.svg',
        logoSmallUrl: 'portal-assets/img/duo-logo-zeile.svg'
      } as HeaderModel;
    } else {
      return {
        languageModels: this.languages ? this.languages.map(l => {
          return { visibleText: l.name, code: l.code } as LanguageModel;
        }) : [],
        navigationTabUrls: [],
        loginDropdownUrls: [
          { useTranslation: true, text: 'COMMON.HEADER.MY_DUO', linkUrl: `${this.env.accountPortalUrl}/login?product-id=4&lang=${this.translateService.currentLang}` }
        ] as Array<LinkModel>,
        loginDropdownProductUrls: [
          { useTranslation: false, text: 'TestDaF', linkUrl: 'https://www.testdaf.de' },
          { useTranslation: false, text: 'TestAS', linkUrl: 'https://www.testas.de' },
          { useTranslation: false, text: 'onSET', linkUrl: 'https://www.onset.de' }
        ] as Array<LinkModel>,
        logoBigUrl: 'portal-assets/img/duo_big.svg',
        logoSmallUrl: 'portal-assets/img/duo-logo-zeile.svg'
      } as HeaderModel;
    }
  }

  private generateHeaderStyleModel(showLanguageDropdown = true, loginDropdownVisible = true): HeaderStyleModel {
    const header = {
      languageDropdownVisible: showLanguageDropdown,
      loginDropdownVisible: loginDropdownVisible
    } as HeaderStyleModel;
    return header;
  }

  // private generateFooter(): FooterModel {
  //   return {
  //     descriptionTitel: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_DUO.TITLE', useTranslation: true } as TextModel,
  //     descriptionText: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_DUO.TEXT', useTranslation: true } as TextModel,
  //     quickLinksTitle: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TITLE', useTranslation: true } as TextModel,
  //     quickLinks: [
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.GERMAN_COURSES', useTranslation: true, linkUrl: `${this.duoUrl}deutschkurse/` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.DUO_FOR_INSTITUTIONS', useTranslation: true, linkUrl: `${this.duoUrl}institutionen/` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.LEARN_WITH_DUO', useTranslation: true, linkUrl: `${this.duoUrl}lernen-mit-duo/` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.SELF_ASSESSMENT', useTranslation: true, linkUrl: `${this.duoUrl}selbsteinschaetzung/` } as LinkModel
  //     ],
  //     serviceLinksTitle: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TITLE', useTranslation: true } as TextModel,
  //     serviceLinks: [
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.FAQ', useTranslation: true, linkUrl: `${this.duoUrl}faq/` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.SYSTEM_REQUIREMENTS', useTranslation: true, linkUrl: `${this.duoUrl}systemvoraussetzungen/` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.BROWSERCHECK', useTranslation: true, linkUrl: `${this.duoUrl}browsercheck` } as LinkModel
  //     ],
  //     contactLinksTitle: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.TITLE', useTranslation: true } as TextModel,
  //     contactLinks: [
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.YOUR_CONTACT_PERSON', useTranslation: true, linkUrl: `${this.duoUrl}ueber-duo/#c524` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONTACT', useTranslation: true, linkUrl: `${this.duoUrl}kontakt/` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.IMPRINT', useTranslation: true, linkUrl: `${this.duoUrl}impressum/` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.DATA_PROTECTION', useTranslation: true, linkUrl: `${this.duoUrl}datenschutz/` } as LinkModel,
  //       { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONDITIONS', useTranslation: true, linkUrl: `${this.duoUrl}agb/` } as LinkModel
  //     ],
  //     gastOfferText: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.GAST_OFFER_TEXT', useTranslation: true } as TextModel,
  //     facebookUrl: 'https://www.facebook.com/DeutschUniOnline/',
  //     youtubeUrl: 'https://www.youtube.com/user/DeutschUniOnline'
  //   } as FooterModel;
  // }

  private generateFooter(): FooterModel {
    return {
      descriptionTitel: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_DUO.TITLE', useTranslation: true } as TextModel,
      descriptionText: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_DUO.TEXT', useTranslation: true } as TextModel,
      quickLinksTitle: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TITLE', useTranslation: true } as TextModel,
      quickLinks: [
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.GERMAN_COURSES', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.GERMAN_COURSE')}/` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.DUO_FOR_INSTITUTIONS', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.INSTITUTIONS')}/` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.LEARN_WITH_DUO', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.LEARNING_WITH_DUO')}/` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.SELF_ASSESSMENT', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.SELF_ASSESSMENT')}/` } as LinkModel
      ],
      serviceLinksTitle: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TITLE', useTranslation: true } as TextModel,
      serviceLinks: [
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.FAQ', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.FAQ')}/` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.SYSTEM_REQUIREMENTS', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.SYSTEMREQUIREMENTS')}/` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.BROWSERCHECK', useTranslation: true, linkUrl: `https://www.deutsch-uni.com/support/konfiguration/index?lang=${this.translateService.currentLang}` } as LinkModel
      ],
      contactLinksTitle: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.TITLE', useTranslation: true } as TextModel,
      contactLinks: [
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.YOUR_CONTACT_PERSON', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.ABOUT_DUO')}/#c524` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONTACT', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.CONTACT')}/` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.IMPRINT', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.IMPRINT')}/` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.DATA_PROTECTION', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.PRIVACY')}/` } as LinkModel,
        { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONDITIONS', useTranslation: true, linkUrl: `${this.duoUrl}${this.translateService.currentLang === 'fr' ? 'de' : this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.GTC')}/` } as LinkModel
      ],
      gastOfferText: { text: 'GAST-PORTAL-NG.FOOTER-COMPONENT.GAST_OFFER_TEXT', useTranslation: true } as TextModel,
      facebookUrl: 'https://www.facebook.com/DeutschUniOnline/',
      youtubeUrl: 'https://www.youtube.com/user/DeutschUniOnline'
    } as FooterModel;
  }

  private generateFloatingContactPanelModel(): FloatingContactPanelModel {
    return {
      contactImgTitle: { text: 'GAST-PORTAL-NG.FLOATING-CONTACT-PANEL.CONTACT_IMG_TEXT', useTranslation: true } as TextModel,
      contactLink: {
        linkUrl: `https://www.deutsch-uni.com/${this.translateService.currentLang}/${this.translateService.instant('GAST-PORTAL-NG.DUO-URL.CONTACT')}/`,
        text: 'GAST-PORTAL-NG.FLOATING-CONTACT-PANEL.CONTACT_LINK_TEXT', useTranslation: true
      } as LinkModel,
      emailIcon: 'fal fa-envelope',
      hiddentText: { text: 'COURSE.UPDATED_FLOATING_PANEL_TEXT', useTranslation: true } as TextModel,
      phoneIcon: 'fal fa-phone-volume',
      phoneNumberString: '+49 234 32 29 770'
    } as FloatingContactPanelModel;
  }
}
